/**
 * 작성자 : 한영광
 * 날짜 : 2025.05.12
 * 설명 : 서비스 보고서 페이지
 */

import styled from 'styled-components';
import ServiceReport from '../components/ServiceReport';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiGet, apiPost } from '../services/_common';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import ReactSignatureCanvas from 'react-signature-canvas';

const Root = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 1rem 0 3rem;
  overflow-y: auto;
  @media (max-width: 480px) {
    padding: 0.5rem;
    padding-bottom: 80px; /* 모바일에서 하단 버튼을 위한 여백 추가 */
  }
  .signImg-container {
    width: 774px;
    margin: 1.5rem auto 0;
    border: 1px solid var(--outline);
    border-radius: 0.5rem;
    padding: 1rem;
    background: white;
  }
  .signImg-title {
    font-weight: 600;
    margin-bottom: 0.75rem;
    font-size: 1.1rem;
    color: var(--primary);
    border-bottom: 1px solid var(--outline);
    padding-bottom: 0.5rem;
  }
  .signImg-container img {
    max-width: 100%;
    max-height: 150px;
    display: block;
    margin: 0 auto;
  }
`;

const SignatureContainer = styled.div`
  margin-top: 20px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px; /* 기본 마진 추가 */

  @media (max-width: 480px) {
    margin-bottom: 40px; /* 모바일에서 더 큰 마진 추가 */
  }
`;

const SignatureCanvasWrapper = styled.div`
  border: 2px dashed var(--primary);
  border-radius: 4px;
  width: 100%;
  max-width: 774px;
  height: 340px;
  background-color: #fff;
  margin-bottom: 10px;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
`;

const SignatureHint = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #999;
  font-size: 16px;
  pointer-events: none;
  z-index: 0;
  opacity: 0.7;

  @media screen and (max-width: 1024px) {
    font-size: 6vw;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 10px;
`;

const Button = styled.button`
  padding: 10px 20px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  font-size: 16px;

  &:hover {
    background-color: #0069d9;
  }

  &.clear {
    background-color: #dc3545;

    &:hover {
      background-color: #c82333;
    }
  }

  @media screen and (max-width: 1024px) {
    padding: 5vw 5vw;
    font-size: 5vw;
  }
`;

const StyledSignatureCanvas = styled(ReactSignatureCanvas)`
  width: 100% !important;
  height: 100% !important;
  position: absolute;
  top: 0;
  left: 0;

  canvas {
    width: 100% !important;
    height: 100% !important;
  }
`;

const initialState: IServiceReport = {
  smIdx: 0,
  cCd: '',
  hCd: '',
  hName: '',
  sCd: '',
  sName: '',
  address: '',
  signManagerName: '',
  signManagerPhone: '',
  signManagerPartname: '',
  companyManagerName: '',
  companyManagerPhone: '',
  mainManagerId: '',
  subManagerId: '',
  mainManagerName: '',
  subManagerName: '',
  mainManagerPhone: '',
  subManagerPhone: '',
  mainManagerSignUrl: '',
  subManagerSignUrl: '',
  bigo: '',
  useMaterial: '',
  maintenanceTitle: '',
  maintenanceCauses: '',
  maintenanceAction: '',
  installDate: ['', '', '', ''],
  installTitle: '',
  installList: [{ name: '', unit: '', qty: '', location: '', bigo: '', gubun: { name: '', gubun: '' }, date1: '', date2: '', date3: '', date4: '' }],
  isInstall: 'Y',
  isMaintenance: 'Y',
  isPaid: 'Y',
  signImg: '',
  scheduleRFList: [],
  completeDate: '',
  writer: '',
  editor: '',
  wDate: '',
  eDate: '',
  isSent: 0,
};

const ServiceReportPage = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState<IServiceReport>(initialState);
  const params = useParams();
  const [dataUrl, setDataUrl] = useState<string>(''); // 이미지 BASE64 URL
  const [isSigned, setSigned] = useState<boolean>(false); // 서명 상태
  const signatureCanvasWrapperRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<ReactSignatureCanvas>(null);
  const originalViewport = 'initial-scale=1.0, user-scalable=no, maximum-scale=1, width=device-width, minimal-ui, viewport-fit=auto';

  useEffect(() => {
    if (params.smIdx) {
      getServiceReport(params.smIdx);
    }
  }, [params.smIdx]);

  // 모바일 화면 최적화
  useEffect(() => {
    if (window.FlutterWebView) return () => {};

    // 기존 viewport meta 태그 찾기
    const $body = document.body as HTMLBodyElement;
    const viewportMeta = document.querySelector('meta[name="viewport"]') as HTMLMetaElement;

    if (!viewportMeta) return () => {};

    // 새로운 viewport설정
    viewportMeta.content = 'width=850';
    $body.style.touchAction = 'auto';
    $body.style.overflowY = 'auto';

    // 컴포넌트 언마운트 시 원래 값으로 복원
    return () => {
      viewportMeta.content = originalViewport;
    };
  }, []);

  const getServiceReport = async (smIdx: string) => {
    const res = await apiGet({ path: `/schedule/report?smIdx=${smIdx}` });
    const { data: resData } = res.data;

    // 조건문 수정 필요
    if (res.status === 200) {
      setForm({
        ...resData,
        installDate: resData.installDate || ['', '', '', ''],
        scheduleRFList: resData.scheduleRFList.map((el: { rfIndex: number; rfUrl: string; smIdx: number }) => ({
          fileId: el.rfIndex,
          file: '',
          fileName: el.rfUrl.slice(el.rfUrl.lastIndexOf('/') + 1),
          fileFormat: el.rfUrl.slice(el.rfUrl.lastIndexOf('.') + 1),
          fUrl: el.rfUrl,
        })),
      });
    } else {
      window.location.href = '/404';
    }
  };

  const onClickSave = async () => {
    if (dataUrl === '') {
      toast.warn(t('서명을 해주세요'));
    } else {
      const base64 = dataUrl.split(',')[1];
      const res = await apiPost({ path: `/schedule/report/sign`, contentType: 'application/json', req: { smIdx: params.smIdx, signImg: base64, cCd: form.cCd, hCd: form.hCd, sCd: form.sCd } });
      if (res.status === 200) {
        toast.success(t('서명이 저장되었습니다'));
        window.location.reload();
      }
    }
  };

  const clearSignature = () => {
    canvasRef.current?.clear();
    setDataUrl('');
    setSigned(false);
  };

  useEffect(() => {
    let prevWidth = 0;
    let prevHeight = 0;

    // 모바일 브라우저의 툴바 show/hide로 발생하는 height-only resize는 무시한다.
    const resizeCanvas = () => {
      if (!signatureCanvasWrapperRef.current || !canvasRef.current) return;

      const canvas = canvasRef.current.getCanvas();
      const nextWidth = signatureCanvasWrapperRef.current.offsetWidth;
      const nextHeight = signatureCanvasWrapperRef.current.offsetHeight;
      const hasPrevSize = prevWidth > 0 && prevHeight > 0;
      const widthChanged = prevWidth !== nextWidth;
      const heightChanged = prevHeight !== nextHeight;

      if (hasPrevSize && !widthChanged && heightChanged) return;
      if (hasPrevSize && !widthChanged && !heightChanged) return;

      const existingImage = !canvasRef.current.isEmpty() ? canvasRef.current.toDataURL('image/png') : '';
      const restoreImage = existingImage ? new Image() : null;
      if (restoreImage) restoreImage.src = existingImage;

      canvas.width = nextWidth;
      canvas.height = nextHeight;
      canvas.style.width = `${nextWidth}px`;
      canvas.style.height = `${nextHeight}px`;

      const ctx = canvas.getContext('2d');
      if (ctx && restoreImage) {
        restoreImage.onload = () => {
          ctx.drawImage(restoreImage, 0, 0, nextWidth, nextHeight);
        };
      }

      prevWidth = nextWidth;
      prevHeight = nextHeight;
    };

    resizeCanvas();
    window.addEventListener('load', resizeCanvas);
    window.addEventListener('resize', resizeCanvas);

    return () => {
      window.removeEventListener('load', resizeCanvas);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  const randomString = Math.random().toString(36).substring(7);

  return (
    <Root>
      <div className='contents' style={{ width: '100%', height: 'auto', display: 'flex', alignItems: 'flex-start' }}>
        <div className='content-container' style={{ display: 'flex', width: '100%', height: 'auto', flexDirection: 'column' }}>
          <ServiceReport form={form} setForm={setForm} isEdit={false} signSrc={dataUrl} isSignView={!false} />
          {form.signImg ? (
            <div className='signImg-container'>
              <div className='signImg-title'>{t('현장 담당자 서명')}</div>
              <img src={`${form.signImg}?v=${randomString}`} alt={t('현장 담당자 서명')} />
            </div>
          ) : (
            <SignatureContainer>
              <SignatureCanvasWrapper ref={signatureCanvasWrapperRef}>
                {!isSigned && <SignatureHint>{t('서명')}</SignatureHint>}
                <StyledSignatureCanvas
                  penColor='black'
                  velocityFilterWeight={1}
                  minWidth={2}
                  maxWidth={4}
                  canvasProps={{
                    className: 'signature-canvas',
                  }}
                  ref={canvasRef}
                  backgroundColor='transparent'
                  clearOnResize={false}
                  onBegin={() => {
                    setSigned(true);
                  }}
                  onEnd={() => {
                    const img = canvasRef.current?.toDataURL('image/jpg') || '';
                    setDataUrl(img);
                  }}
                />
              </SignatureCanvasWrapper>

              <ButtonContainer>
                <Button className='clear' onClick={clearSignature}>
                  {t('지우기')}
                </Button>
                <Button onClick={onClickSave}>{t('확인')}</Button>
              </ButtonContainer>
            </SignatureContainer>
          )}
        </div>
      </div>
    </Root>
  );
};

export default ServiceReportPage;
